import {
  CalidadCodigoRes,
  EstadoCondicionCc,
  EstadoGateCc,
  EstadoProyectoCc,
  EstadoSincronizacionCc,
  FiltroCalidadCodigo,
  TipoReporteCc,
} from "src/app/shared/model/calidad-codigo/calidad-codigo.model";

/**
 * Cómo se lee en pantalla cada estado del módulo, con qué clase y con qué
 * icono (mismo patrón que bsc/bsc-estados.ts). Las clases se pintan en
 * calidad-codigo.shared.css sobre .cc-insignia, con los tokens --cc-* para
 * los tres temas.
 */
export interface EstadoCc<T extends string> {
  valor: T;
  texto: string;
  clase: string;
  icono: string;
}

/** El veredicto del proyecto (ProyectoResumen.estado). */
export const ESTADO_PROYECTO: EstadoCc<EstadoProyectoCc>[] = [
  { valor: "PASA", texto: "Pasa", clase: "is-pasa", icono: "check" },
  { valor: "NO_PASA", texto: "No pasa", clase: "is-no-pasa", icono: "close" },
  { valor: "SIN_ANALISIS", texto: "Sin análisis", clase: "is-neutro", icono: "block" },
];

/** El gate de un análisis guardado (AnalisisResumen.estado). */
export const ESTADO_GATE: EstadoCc<EstadoGateCc>[] = [
  { valor: "OK", texto: "Pasa", clase: "is-pasa", icono: "check" },
  { valor: "ERROR", texto: "No pasa", clase: "is-no-pasa", icono: "close" },
  { valor: "NONE", texto: "Sin gate", clase: "is-neutro", icono: "block" },
];

/** Cada criterio del gate (Condicion.estado). */
export const ESTADO_CONDICION: EstadoCc<EstadoCondicionCc>[] = [
  { valor: "OK", texto: "Pasa", clase: "is-pasa", icono: "check" },
  { valor: "ERROR", texto: "Falla", clase: "is-no-pasa", icono: "close" },
  { valor: "SIN_DATO", texto: "Sin dato", clase: "is-neutro", icono: "info" },
  { valor: "IGNORADA", texto: "Ignorada", clase: "is-aviso", icono: "visibility_off" },
];

/** Una pasada de sincronización (Sincronizacion.estado y EstadoConexion.ultimaSyncEstado). */
export const ESTADO_SINCRONIZACION: EstadoCc<EstadoSincronizacionCc>[] = [
  { valor: "OK", texto: "Correcta", clase: "is-pasa", icono: "check_circle" },
  { valor: "PARCIAL", texto: "Parcial", clase: "is-aviso", icono: "error_outline" },
  { valor: "ERROR", texto: "Con error", clase: "is-no-pasa", icono: "cancel" },
  { valor: "EN_CURSO", texto: "En curso", clase: "is-info", icono: "sync" },
];

/** Los chips del tablero: resumen y filtro a la vez; el punto lleva el color del estado. */
export const FILTROS_TABLERO: { valor: FiltroCalidadCodigo; texto: string; punto: string }[] = [
  { valor: "TODOS", texto: "Todos", punto: "" },
  { valor: "NO_PASA", texto: "No pasa", punto: "is-no-pasa" },
  { valor: "PASA", texto: "Pasa", punto: "is-pasa" },
  { valor: "SIN_ANALISIS", texto: "Sin análisis", punto: "is-neutro" },
  { valor: "DESACTUALIZADOS", texto: "Desactualizados", punto: "is-aviso" },
];

export const TIPO_REPORTE: Record<TipoReporteCc, string> = {
  PROYECTO: "Proyecto",
  QA: "Para QA",
};

/**
 * El origen de una pasada (cos_sonar_sincronizacion.origen); REPORTE es la que corre antes de congelar un reporte.
 * Corto como los demás: la celda de la bitácora mide 120 px y lo que se recorta es el usuario, no el origen.
 */
export const ORIGEN_SINCRONIZACION: Record<string, string> = {
  PROGRAMADA: "Programada",
  MANUAL: "Manual",
  CARGA_INICIAL: "Carga inicial",
  REPORTE: "Reporte",
};

/** Las categorías de evento que manda SonarQube, en español. */
export const CATEGORIA_EVENTO: Record<string, string> = {
  QUALITY_GATE: "Quality Gate",
  QUALITY_PROFILE: "Perfil de calidad",
  VERSION: "Versión",
  SQ_UPGRADE: "Actualización de SonarQube",
  DEFINITION_CHANGE: "Cambio de definición",
  ISSUE_DETECTION: "Detección de hallazgos",
  OTHER: "Otro",
};

/**
 * SonarQube rechazó la credencial del COS: el error de una pasada lo dice (401, «credencial» o «unauthorized»). Una
 * sola regla para la franja del tablero y para el aviso y el pie de un reporte que salió con la última foto guardada.
 */
export function credencialRechazada(error: string | null | undefined): boolean {
  return /401|credencial|unauthorized/i.test(error ?? "");
}

/** El estado que corresponde a un valor; el último de la lista (el neutro) si no se conoce. */
export function estadoDe<T extends string>(lista: EstadoCc<T>[], valor: string | null | undefined): EstadoCc<T> {
  return lista.find((e) => e.valor === valor) ?? lista.at(-1) ?? lista[0];
}

/**
 * Lo que se dice cuando el servidor contesta con codigoError: AUTH-001
 * viaja en un 200 (ningún interceptor lo ve); lo demás trae su mensaje.
 */
export function textoDeError(res: CalidadCodigoRes | null | undefined, que: string): string {
  if (res?.codigoError === "AUTH-001") {
    return "Tu sesión caducó. Vuelve a entrar.";
  }
  return res?.mensajeError || `No se pudo ${que}.`;
}
